import { useCallback, useMemo, useState } from 'react';
import type { FormInstance } from 'antd';
import { useNotify } from 'react-admin';
import { toMessages } from '@src/helpers/serverErrors';

export type FieldError = { name: (string | number)[]; errors: string[] };

/** DRF keys that describe the request as a whole rather than one field */
const GENERAL_KEYS = new Set(['non_field_errors', 'detail', 'unknown', 'trigger']);

/**
 * Turns a DRF 400 body into inline field errors.
 *
 * Anything the form has no field for — `non_field_errors`, or a key the
 * caller did not declare — is raised as a notification instead, so a
 * validation failure can never land somewhere the user cannot see it.
 *
 * `fieldErrors` is meant to be handed to <NetorcaForm serverErrors=…> so the
 * error summary covers server-side failures as well as client-side ones.
 */
const useServerErrors = (form: FormInstance, knownFields?: string[]) => {
    const notify = useNotify();
    const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

    const knownKey = knownFields ? knownFields.join('|') : '';
    const known = useMemo(() => (knownKey ? new Set(knownKey.split('|')) : null), [knownKey]);

    const clear = useCallback(() => setFieldErrors([]), []);

    const apply = useCallback(
        (error: any, fallback = 'Something went wrong') => {
            const body = error?.body;
            if (!body || typeof body !== 'object' || Array.isArray(body)) {
                notify(error?.message || fallback, 'error');
                return;
            }

            const fields: FieldError[] = [];
            const general: string[] = [];

            Object.entries<any>(body).forEach(([key, value]) => {
                const messages = toMessages(value);
                if (GENERAL_KEYS.has(key) || (known && !known.has(key))) general.push(...messages);
                else fields.push({ name: key.split('.'), errors: messages });
            });

            if (fields.length) form.setFields(fields);
            setFieldErrors(fields);
            general.forEach((message) => notify(message, 'error'));
            if (!fields.length && !general.length) notify(fallback, 'error');
        },
        [form, notify, known],
    );

    return { apply, fieldErrors, clear };
};

export default useServerErrors;
